/**
 * Google Tag Manager Adapter
 *
 * Handles event tracking via Google Tag Manager dataLayer.
 * Works with GA4, Google Ads, and other GTM tags.
 */

'use client';

import type {
  TrackingAdapter,
  TrackingEvent,
  TrackingIdentity,
} from '../types';
import { getGTMEventName } from '../events';

/**
 * GTM tracking adapter
 */
export class GTMAdapter implements TrackingAdapter {
  name = 'gtm';

  /**
   * Check if GTM dataLayer is available
   */
  isAvailable(): boolean {
    return typeof window !== 'undefined' && Array.isArray(window.dataLayer);
  }

  /**
   * Track an event to GTM dataLayer
   */
  async track(event: TrackingEvent): Promise<void> {
    if (!this.isAvailable()) {
      console.warn('GTM dataLayer not available');
      return;
    }

    try {
      const eventName = getGTMEventName(event.eventName);
      const eventData = await this.formatEventData(event);

      if (window.dataLayer) {
        window.dataLayer.push({
          event: eventName,
          ...eventData,
        });
      }
    } catch (error) {
      console.error('GTM tracking error:', error);
      throw error;
    }
  }

  /**
   * Identify a user in GTM
   */
  async identify(identity: TrackingIdentity): Promise<void> {
    if (!this.isAvailable()) {
      console.warn('GTM dataLayer not available');
      return;
    }

    try {
      const userData: Record<string, any> = {};

      if (identity.userId) {
        userData.user_id = identity.userId;
      }

      if (identity.email) {
        // CRITICAL: Use SHA-256 for enhanced conversions (Google Ads, GA4)
        userData.user_email_hash = await this.hashSHA256(identity.email);
      }

      if (identity.phone) {
        // CRITICAL: Use SHA-256 for enhanced conversions
        userData.user_phone_hash = await this.hashSHA256(
          identity.phone.replace(/\D/g, ''),
        );
      }

      // Push user properties to dataLayer
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'user_identified',
          user_data: userData,
          user_properties: {
            first_name: identity.firstName,
            last_name: identity.lastName,
            ...identity.properties,
          },
        });
      }
    } catch (error) {
      console.error('GTM identify error:', error);
      throw error;
    }
  }

  /**
   * Format event data for GTM (GA4 format)
   */
  private async formatEventData(
    event: TrackingEvent,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = {
      ...event.properties,
    };

    // Add product info (GA4 items format)
    if (event.product) {
      data.items = [
        {
          item_id: event.product.productId,
          item_name: event.product.name,
          price: event.product.price,
          currency: event.product.currency || 'USD',
          item_category: event.product.category,
          item_brand: event.product.brand,
          quantity: event.product.quantity || 1,
        },
      ];

      data.currency = event.product.currency || 'USD';
      data.value = event.product.price * (event.product.quantity || 1);
    }

    // Add order/transaction info (GA4 ecommerce format)
    if (event.order) {
      data.transaction_id = event.order.orderId;
      data.value = event.order.total;
      data.currency = event.order.currency;
      data.tax = event.order.tax;
      data.shipping = event.order.shipping;
      data.coupon = event.order.coupon;

      if (event.order.products) {
        data.items = event.order.products.map((p) => ({
          item_id: p.productId,
          item_name: p.name,
          price: p.price,
          currency: p.currency || event.order?.currency || 'USD',
          item_category: p.category,
          item_brand: p.brand,
          item_variant: p.variant,
          quantity: p.quantity || 1,
        }));
      }
    }

    // Add value if present and not already set
    if (event.value !== undefined && data.value === undefined) {
      data.value = event.value;
    }

    // Add page context
    if (event.url) {
      data.page_location = event.url;
    }

    if (event.pageTitle) {
      data.page_title = event.pageTitle;
    }

    // Add timestamp
    data.timestamp = event.timestamp || new Date().toISOString();

    return data;
  }

  /**
   * Hash value with SHA-256 for privacy and enhanced conversions
   * CRITICAL: This is required for Google Ads enhanced conversions to work properly
   *
   * @see https://support.google.com/google-ads/answer/9888656
   */
  private async hashSHA256(value: string): Promise<string> {
    // Normalize: lowercase and trim
    const normalized = value.toLowerCase().trim();

    // Check if crypto.subtle is available (HTTPS required)
    if (typeof crypto === 'undefined' || !crypto.subtle) {
      console.warn('crypto.subtle not available (HTTPS required for hashing)');
      return normalized; // Fallback to normalized value
    }

    try {
      // Convert string to ArrayBuffer
      const encoder = new TextEncoder();
      const data = encoder.encode(normalized);

      // Hash with SHA-256
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);

      // Convert ArrayBuffer to hex string
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

      return hashHex;
    } catch (error) {
      console.error('SHA-256 hashing failed:', error);
      return normalized; // Fallback to normalized value
    }
  }
}

// Extend window type for GTM
declare global {
  interface Window {
    dataLayer?: Array<Record<string, any>>;
  }
}
